import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, img, useGet, waLink } from "@/lib/api";
import { useShop } from "@/lib/store";
import { FileUploader } from "./FileUploader";
import { GarmentPreview, COLORS } from "./GarmentPreview";

export const GARMENTS = ["T-Shirt", "Oversized T-Shirt", "Hoodie", "Sweatshirt"];
export const PLACEMENTS = ["Front", "Back", "Left Chest", "Right Chest", "Sleeve", "Full Front and Back"];
const ORDER_TYPES = ["Personal Custom Order", "Bulk Printing", "Wholesale", "Business Merchandise"];
const SIZES = ["S", "M", "L", "XL", "XXL"];

const F = ({ label, children, className = "" }) => <label className={`block ${className}`}><span className="label">{label}</span>{children}</label>;

function Success({ id, kind, whatsapp }) {
  return (
    <div className="border border-neon p-8 sm:p-12 reveal" data-testid="enquiry-success">
      <CheckCircle2 className="w-12 h-12 text-neon" />
      <h3 className="font-display text-4xl sm:text-5xl mt-6">REQUEST RECEIVED.</h3>
      <p className="text-white/70 mt-3 max-w-lg">Your {kind === "wholesale" ? "wholesale enquiry" : "custom printing request"} and artwork are saved securely. Our team will review and share a quotation shortly.</p>
      <p className="mt-6 font-mono text-sm text-grey">ENQUIRY ID</p>
      <p className="font-display text-5xl text-neon tracking-wide" data-testid="enquiry-id">{id}</p>
      <div className="flex flex-wrap gap-3 mt-8">
        <a href={waLink(whatsapp, `Hi DEN & THREADS, I just submitted enquiry ${id}.`)} target="_blank" rel="noreferrer" className="btn-neon" data-testid="enquiry-whatsapp-button">Chat on WhatsApp</a>
        <Link to="/shop" className="btn-ghost">Continue Shopping</Link>
      </div>
    </div>
  );
}

export function EnquiryForm({ variant = "studio", design: initialDesign = "" }) {
  const wholesale = variant === "wholesale";
  const { settings } = useShop();
  const { data: anime = [] } = useGet(variant === "studio" ? "/anime" : null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [breakdown, setBreakdown] = useState({});
  const [f, setF] = useState({
    order_type: wholesale ? "Wholesale" : initialDesign ? "Personal Custom Order" : "Personal Custom Order",
    full_name: "", business_name: "", mobile: "", whatsapp: "", email: "", city: "",
    product_type: wholesale ? "Oversized T-Shirt" : "Oversized T-Shirt", garment_color: "Jet Black", placement: initialDesign ? "Back" : "Front",
    size: "L", quantity: wholesale ? 50 : 1, instructions: "", design_slug: initialDesign,
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const bdSum = Object.values(breakdown).reduce((a, b) => a + (Number(b) || 0), 0);
  const qty = bdSum || Number(f.quantity) || 0;
  const chosen = anime.find((a) => a.slug === f.design_slug);
  const art = files.find((x) => x.preview)?.preview || (chosen && img(chosen.art));

  const submit = async (e) => {
    e.preventDefault();
    if (files.some((x) => !x.path && !x.error)) return toast.error("Please wait for uploads to finish");
    const uploaded = files.filter((x) => x.path).map(({ path, name, content_type, size }) => ({ path, name, content_type, size }));
    if (!uploaded.length && !f.design_slug) return toast.error(wholesale ? "Please upload your artwork or logo" : "Upload artwork or choose an anime design");
    setBusy(true);
    try {
      const sb = Object.fromEntries(Object.entries(breakdown).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)]));
      if (!bdSum && variant === "studio") sb[f.size] = qty;
      const body = { ...f, kind: wholesale ? "wholesale" : "custom", quantity: qty, size_breakdown: sb, files: uploaded, whatsapp: f.whatsapp || f.mobile };
      delete body.size;
      const r = (await http.post("/enquiries", body)).data;
      setDone(r.enquiry_id);
      window.scrollTo({ top: document.getElementById("enquiry-anchor")?.offsetTop - 80, behavior: "smooth" });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (done) return <div id="enquiry-anchor"><Success id={done} kind={wholesale ? "wholesale" : "custom"} whatsapp={settings.whatsapp} /></div>;

  return (
    <form onSubmit={submit} id="enquiry-anchor" className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16" data-testid={`enquiry-form-${variant}`}>
      <div className="space-y-6 lg:sticky lg:top-24 self-start">
        {variant === "studio" ? <GarmentPreview garment={f.product_type} color={f.garment_color} placement={f.placement} art={art} /> : (
          <div className="bg-[#171717] p-6 border border-[#2c2c2c]">
            <p className="eyebrow mb-3">{wholesale ? "Artwork / Logo Upload" : "Your Artwork"}</p>
            <FileUploader files={files} setFiles={setFiles} testid={`${variant}-upload`} />
          </div>
        )}
        {variant === "studio" && anime.length > 0 && (
          <div>
            <span className="label">Or pick from Anime After Dark</span>
            <div className="grid grid-cols-4 gap-2">
              {anime.map((a) => (
                <button type="button" key={a.slug} onClick={() => setF((s) => ({ ...s, design_slug: s.design_slug === a.slug ? "" : a.slug }))} data-testid={`studio-anime-${a.slug}`}
                  className={`aspect-square overflow-hidden border-2 transition-colors ${f.design_slug === a.slug ? "border-neon" : "border-transparent hover:border-white/40"}`}>
                  <img src={img(a.art)} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
            {chosen && <p className="text-xs text-neon font-mono mt-2" data-testid="studio-selected-design">SELECTED: {chosen.title.toUpperCase()}</p>}
          </div>
        )}
      </div>

      <div className="space-y-8">
        {variant === "studio" && (
          <section className="space-y-5">
            <p className="eyebrow">01 — Build your garment</p>
            <div><span className="label">Garment</span><div className="flex flex-wrap gap-2">{GARMENTS.map((g) => <button type="button" key={g} onClick={() => set("product_type")(g)} data-testid={`garment-${g.toLowerCase().replace(/\s/g, "-")}`} className={`chip ${f.product_type === g ? "on" : ""}`}>{g}</button>)}</div></div>
            <div><span className="label">Colour — {f.garment_color}</span><div className="flex flex-wrap gap-2">{Object.entries(COLORS).map(([n, c]) => <button type="button" key={n} title={n} onClick={() => set("garment_color")(n)} data-testid={`color-${n.toLowerCase().replace(/\s/g, "-")}`} className={`w-9 h-9 border-2 transition-transform hover:scale-110 ${f.garment_color === n ? "border-neon" : "border-[#333]"}`} style={{ background: c }} />)}</div></div>
            <div><span className="label">Print placement</span><div className="flex flex-wrap gap-2">{PLACEMENTS.map((p) => <button type="button" key={p} onClick={() => set("placement")(p)} data-testid={`placement-${p.toLowerCase().replace(/\s/g, "-")}`} className={`chip ${f.placement === p ? "on" : ""}`}>{p}</button>)}</div></div>
            <div className="grid grid-cols-2 gap-4">
              <F label="Size"><select className="field" value={f.size} onChange={set("size")} data-testid="studio-size-select">{SIZES.map((s) => <option key={s}>{s}</option>)}</select></F>
              <F label="Quantity"><input type="number" min="1" className="field" value={f.quantity} onChange={set("quantity")} data-testid="studio-quantity-input" disabled={bdSum > 0} /></F>
            </div>
            <div><span className="label">Upload artwork</span><FileUploader files={files} setFiles={setFiles} testid="studio-upload" /></div>
          </section>
        )}

        {variant !== "studio" && (
          <section className="space-y-5">
            <p className="eyebrow">01 — {wholesale ? "Bulk requirement" : "What should we print?"}</p>
            {!wholesale && <div><span className="label">Order type</span><div className="flex flex-wrap gap-2">{ORDER_TYPES.map((o) => <button type="button" key={o} onClick={() => set("order_type")(o)} data-testid={`order-type-${o.toLowerCase().replace(/\s/g, "-")}`} className={`chip ${f.order_type === o ? "on" : ""}`}>{o}</button>)}</div></div>}
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Product type"><select className="field" value={f.product_type} onChange={set("product_type")} data-testid="product-type-select">{[...GARMENTS, ...(wholesale ? ["Cargo Pants", "Jeans", "Mixed Apparel"] : [])].map((g) => <option key={g}>{g}</option>)}</select></F>
              <F label="Garment colour"><select className="field" value={f.garment_color} onChange={set("garment_color")} data-testid="garment-color-select">{Object.keys(COLORS).map((c) => <option key={c}>{c}</option>)}</select></F>
              <F label={wholesale ? "Printing requirements" : "Printing placement"}><select className="field" value={f.placement} onChange={set("placement")} data-testid="placement-select">{PLACEMENTS.map((p) => <option key={p}>{p}</option>)}</select></F>
              <F label="Total quantity"><input type="number" min="1" className="field" value={bdSum || f.quantity} onChange={set("quantity")} disabled={bdSum > 0} data-testid="quantity-input" /></F>
            </div>
          </section>
        )}

        <section>
          <span className="label">Size-wise quantity breakdown (optional)</span>
          <div className="grid grid-cols-5 gap-2">
            {SIZES.map((s) => (
              <div key={s} className="text-center">
                <span className="font-mono text-xs text-grey">{s}</span>
                <input type="number" min="0" className="field text-center mt-1 px-1" value={breakdown[s] || ""} onChange={(e) => setBreakdown((b) => ({ ...b, [s]: e.target.value }))} data-testid={`breakdown-${s}`} />
              </div>
            ))}
          </div>
          {bdSum > 0 && <p className="text-xs font-mono text-neon mt-2">TOTAL: {bdSum} PCS</p>}
        </section>

        <section className="space-y-4">
          <p className="eyebrow">02 — Your details</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {wholesale && <F label="Business name *" className="sm:col-span-2"><input required className="field" value={f.business_name} onChange={set("business_name")} data-testid="business-name-input" /></F>}
            <F label={wholesale ? "Contact person *" : "Full name *"}><input required className="field" value={f.full_name} onChange={set("full_name")} data-testid="full-name-input" /></F>
            <F label="Email *"><input required type="email" className="field" value={f.email} onChange={set("email")} data-testid="email-input" /></F>
            <F label="Mobile number *"><input required className="field" value={f.mobile} onChange={set("mobile")} placeholder="10-digit mobile" data-testid="mobile-input" /></F>
            <F label="WhatsApp number"><input className="field" value={f.whatsapp} onChange={set("whatsapp")} placeholder="Same as mobile if empty" data-testid="whatsapp-input" /></F>
            <F label="City" className="sm:col-span-2"><input className="field" value={f.city} onChange={set("city")} data-testid="city-input" /></F>
          </div>
          <F label="Additional instructions"><textarea rows={4} className="field" value={f.instructions} onChange={set("instructions")} placeholder="Print size, colours, deadline, fabric GSM…" data-testid="instructions-input" /></F>
        </section>

        <button disabled={busy} className="btn-neon w-full py-5 text-sm" data-testid="enquiry-submit-button">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{wholesale ? "GET WHOLESALE QUOTE" : "SUBMIT PRINT REQUEST"}
        </button>
        <p className="text-xs text-grey font-mono">No payment now — we review your artwork and send a quotation on WhatsApp/email.</p>
      </div>
    </form>
  );
}
